import { SQSClient, SendMessageCommand } from '@aws-sdk/client-sqs';
import { TelemetryEvent, DisplaySubmissionEvent } from './types';

// TODO: Replace SEARCH_QUEUE_URL with actual queue URL after infrastructure deployment
// Expected format: https://sqs.us-east-1.amazonaws.com/773926183863/Development-DynamoDB-SearchQueue-ABC123
const SEARCH_QUEUE_URL = process.env['SEARCH_QUEUE_URL'] ?? '';

// Must match the key used in getSessionId() in libs/ui-shared.
export const CORRELATION_ID_KEY = 'peerless-session-id';

function getCorrelationId(): string {
  if (typeof sessionStorage === 'undefined') return 'unknown';
  return sessionStorage.getItem(CORRELATION_ID_KEY) ?? 'unknown';
}

function createUUID(): string {
  return globalThis?.crypto?.randomUUID?.() ?? '';
}

let sqsClient: SQSClient | null = null;
function getSQSClient(): SQSClient {
  if (!sqsClient) {
    sqsClient = new SQSClient({});
  }
  return sqsClient;
}

/**
 * Emit a telemetry event to SQS for search analytics.
 * Fire-and-forget pattern: does not await or throw errors.
 *
 * @param event - Telemetry event data (timestamp, requestId, and sessionId are auto-generated)
 * @param locale - Locale code (e.g., 'en_US', 'fr_CA')
 * @param region - Region code (e.g., 'US', 'CA', 'GB')
 */
export function emitTelemetryEvent(
  event: Omit<
    TelemetryEvent,
    'type' | 'timestamp' | 'requestId' | 'sessionId' | 'locale' | 'region'
  >,
  locale: string,
  region: string
): void {
  const message: TelemetryEvent = {
    ...event,
    type: 'telemetry',
    timestamp: new Date().toISOString(),
    requestId: createUUID(),
    sessionId: getCorrelationId(),
    locale,
    region,
  };

  // Mock mode: log to console when queue URL is not configured
  if (!SEARCH_QUEUE_URL) {
    console.log('[Telemetry Mock]', JSON.stringify(message, null, 2));
    return;
  }

  // Fire-and-forget: send to SQS without await, catch errors silently
  getSQSClient()
    .send(
      new SendMessageCommand({
        QueueUrl: SEARCH_QUEUE_URL,
        MessageBody: JSON.stringify(message),
      })
    )
    .catch(err => {
      console.warn(
        `[Search Events] Failed to emit telemetry event (type: ${message.type}, requestId: ${message.requestId}):`,
        err.message
      );
    });
}

/**
 * Emit a display submission event to SQS for unknown display tracking.
 * Fire-and-forget pattern: does not await or throw errors.
 *
 * @param event - Display submission data (timestamp, requestId, and sessionId are auto-generated)
 * @param locale - Locale code (e.g., 'en_US', 'fr_CA')
 * @param region - Region code (e.g., 'US', 'CA', 'GB')
 */
export function emitDisplaySubmission(
  event: Omit<
    DisplaySubmissionEvent,
    'type' | 'timestamp' | 'requestId' | 'sessionId' | 'locale' | 'region'
  >,
  locale: string,
  region: string
): void {
  const message: DisplaySubmissionEvent = {
    ...event,
    type: 'submission',
    timestamp: new Date().toISOString(),
    requestId: createUUID(),
    sessionId: getCorrelationId(),
    locale,
    region,
  };

  // Mock mode: log to console when queue URL is not configured
  if (!SEARCH_QUEUE_URL) {
    console.log('[Submission Mock]', JSON.stringify(message, null, 2));
    return;
  }

  // Fire-and-forget: send to SQS without await, catch errors silently
  getSQSClient()
    .send(
      new SendMessageCommand({
        QueueUrl: SEARCH_QUEUE_URL,
        MessageBody: JSON.stringify(message),
      })
    )
    .catch(err => {
      console.warn(
        `[Search Events] Failed to emit display submission (type: ${message.type}, requestId: ${message.requestId}):`,
        err.message
      );
    });
}
